/**
 * Stage a matched shipping-label PDF into the mixed print queue (carrier-pdf parts).
 * Used by Labels panel and Pack Wave day assembly.
 */

import { putPrintQueueBlob } from '../services/printQueueStore';
import { getDefaultQzSettings, loadQzPrintSettings } from '../services/qzPrintService';
import { getQueueOrderKey } from '../services/printQueueModel';
import {
  classifyOrderWeekProximity,
  splitShippingLabelPdf,
} from '../services/shippingLabelPdfMatchService';

/**
 * @param {{
 *   sourceBlob: Blob,
 *   pageMatches: object[],
 *   pageAssignments: Record<number, string>,
 *   candidateOrders: object[],
 *   workingDeliveryDate?: string,
 *   preferredOrderKeys?: string[],
 *   copies?: number,
 *   enqueueCarrierLabel: Function,
 *   selectedPageNumbers?: Set<number>|null,
 * }} opts
 */
export async function stageMatchedShippingLabelPdfToQueue(opts) {
  const {
    sourceBlob,
    pageMatches = [],
    pageAssignments = {},
    candidateOrders = [],
    workingDeliveryDate = '',
    preferredOrderKeys = [],
    copies = 1,
    enqueueCarrierLabel,
    selectedPageNumbers = null,
  } = opts;
  if (typeof enqueueCarrierLabel !== 'function') {
    throw new Error('Print queue carrier staging is not available.');
  }
  if (!(sourceBlob instanceof Blob) || !sourceBlob.size) {
    throw new Error('A shipping-label PDF file is required.');
  }

  const selectedPages =
    selectedPageNumbers instanceof Set
      ? selectedPageNumbers
      : new Set(
          (pageMatches || []).map((page) => Number(page.pageNumber)).filter(Boolean),
        );

  const assignments = (pageMatches || [])
    .filter((page) => selectedPages.has(page.pageNumber))
    .map((page) => ({
      pageNumber: page.pageNumber,
      orderKey: pageAssignments[page.pageNumber] || page.suggestedOrderKey || '',
    }));

  if (!assignments.length) {
    throw new Error('No shipping-label pages are ready to stage.');
  }
  if (assignments.some((assignment) => !assignment.orderKey)) {
    throw new Error('Assign every selected shipping-label page to an order before staging.');
  }

  const split = await splitShippingLabelPdf(sourceBlob, assignments);
  const proximityRank = { active: 0, adjacent: 1, unknown: 2, outside: 3 };
  const findOrder = (orderKey) =>
    candidateOrders.find(
      (candidate) => getQueueOrderKey(candidate) === String(orderKey || ''),
    ) ||
    pageMatches
      .flatMap((page) => page.matches || [])
      .find((match) => match.orderKey === orderKey)?.order || {
      source_id: orderKey,
    };

  const waveRank = new Map(
    (preferredOrderKeys || [])
      .map((key) => String(key || '').replace(/^#/, '').trim())
      .filter(Boolean)
      .map((key, index) => [key, index]),
  );

  const orderedSplit = [...split].sort((left, right) => {
    const leftWave = waveRank.has(String(left.orderKey))
      ? waveRank.get(String(left.orderKey))
      : Number.POSITIVE_INFINITY;
    const rightWave = waveRank.has(String(right.orderKey))
      ? waveRank.get(String(right.orderKey))
      : Number.POSITIVE_INFINITY;
    if (leftWave !== rightWave) return leftWave - rightWave;

    const leftProx =
      classifyOrderWeekProximity(findOrder(left.orderKey), workingDeliveryDate) ||
      'unknown';
    const rightProx =
      classifyOrderWeekProximity(findOrder(right.orderKey), workingDeliveryDate) ||
      'unknown';
    const byWeek =
      (proximityRank[leftProx] ?? 9) - (proximityRank[rightProx] ?? 9);
    if (byWeek !== 0) return byWeek;
    return String(left.orderKey).localeCompare(String(right.orderKey));
  });

  const qzSettings = loadQzPrintSettings();
  const routing = qzSettings.shippingLabel || getDefaultQzSettings('shippingLabel');
  const staged = [];
  for (const part of orderedSplit) {
    const blobRef = `carrier-order-${part.orderKey}-${
      globalThis.crypto?.randomUUID?.() || Date.now()
    }`;
    await putPrintQueueBlob(blobRef, part.blob, {
      orderKey: part.orderKey,
      pages: part.pageNumbers,
    });
    const order = findOrder(part.orderKey);
    enqueueCarrierLabel({
      order,
      blobRef,
      pageCount: part.pageCount,
      copies: Math.max(1, Number(copies) || 1),
      routing,
      label: `Order ${part.orderKey} GoDEX carrier label`,
    });
    staged.push(part.orderKey);
  }
  return {
    orderCount: staged.length,
    orderKeys: staged,
  };
}
